import { useLazyQuery } from '@apollo/client';
import {
  GET_DASHBOARD_METRIC_IDEAS,
  Idea,
  IdeaMetric,
  User,
} from '@frontend/data-access';
import { IdeaChannelDetail } from '@frontend/feature-idea-channel';
import { UserAvatar } from '@frontend/shared/ui';
import { Avatar, Modal, Table } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';

interface ChannelMetricsProps {
  channelId?: string;
  projectId?: string;
  challengeId?: string;
  topicId?: string;
  businessArea?: string;
  countryId?: string;
  metricId?: string;
}

const DEFAULT_LIMIT = 10;

export function MetricIdeas({
  channelId,
  projectId,
  challengeId,
  topicId,
  businessArea,
  countryId,
  metricId,
}: ChannelMetricsProps) {
  const { t } = useTranslation('common');

  const [page, setPage] = useState(1);
  const [getData, { data, loading }] = useLazyQuery<{
    dashboardMetricIdeas: {
      items: Idea[];
      total: number;
    };
  }>(GET_DASHBOARD_METRIC_IDEAS);
  const [selectedId, setSelectedId] = useState<string>();
  const [isOpenModal, setIsOpenModal] = useState(false);

  const handleClickOpen = (record: Idea) => {
    setSelectedId(record.id);
    setIsOpenModal(true);
  };

  const handleClickClose = () => {
    setSelectedId(undefined);
    setIsOpenModal(false);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const columns = useMemo(() => {
    const staticColumns = [
      {
        width: 200,
        title: t('idea_name'),
        dataIndex: 'name',
        key: 'name',
        render: (text: string, record: Idea) => (
          <div className="flex gap-2 items-center">
            <div className="!w-8">
              <Avatar
                shape="square"
                alt={record?.name}
                src={record?.coverPhoto?.url || record?.coverPhotoUrl?.imageUrl}
              />
            </div>
            <div
              onClick={() => handleClickOpen(record)}
              className="max-w-48 line-clamp-2 hover:text-primary-600 cursor-pointer"
            >
              {record.name}
            </div>
          </div>
        ),
      },
      {
        width: 200,
        title: t('created_by'),
        dataIndex: 'createdBy',
        key: 'createdBy',
        render: (createdBy: User) => (
          <div className="flex items-center gap-2">
            <UserAvatar user={createdBy} />
            <span>{createdBy.fullName}</span>
          </div>
        ),
      },
    ];

    if (metricId) {
      return [
        ...staticColumns,
        {
          title: t('metric'),
          dataIndex: 'metric',
          key: 'metric',
          render: (_: unknown, data: Idea) => (
            <div className="flex flex-wrap gap-1">
              {data.metrics?.find((i) => i.metric.id === metricId)?.metric.name}
            </div>
          ),
        },
        {
          title: t('target_amount_benefit'),
          dataIndex: 'target',
          key: 'target',
          render: (_: unknown, record: Idea) => {
            const metric = record.metrics?.find(
              (i) => i.metric.id === metricId
            );
            if (metric?.target)
              return (
                <span>
                  {formatCurrency(metric?.target) || '-'}{' '}
                  {metric?.metric?.unit?.label || ''}
                </span>
              );
            else return '-';
          },
        },
        {
          title: t('actual_result'),
          dataIndex: 'result',
          key: 'result',
          render: (_: unknown, record: Idea) => {
            const metric = record.metrics?.find(
              (i) => i.metric.id === metricId
            );
            if (metric?.result)
              return (
                <span>
                  {formatCurrency(metric?.result) || '-'}{' '}
                  {metric?.metric?.unit?.label || ''}
                </span>
              );
            else return '-';
          },
        },
        {
          title: t('phase'),
          dataIndex: 'currentPhase',
          key: 'currentPhase',
          render: (_: unknown, record: Idea) => {
            return (
              <span>
                {record.currentPhase ? record.currentPhase?.phase.title : '-'}
              </span>
            );
          },
        },
      ];
    } else
      return [
        ...staticColumns,
        {
          title: t('selected_projects'),
          dataIndex: 'projects',
          key: 'projects',
          render: (record: IdeaMetric[], data: Idea) => {
            const uniqProjects =
              data.metrics.filter(
                (m, index, self) =>
                  index ===
                  self.findIndex((t) => t.project?.id === m.project?.id)
              ) || [];
            return (
              <div className="flex flex-wrap gap-1">
                {uniqProjects
                  .map((metric) =>
                    metric?.project === null
                      ? t('no_project')
                      : metric.project?.name
                  )
                  ?.join(', ')}
              </div>
            );
          },
        },
        {
          title: t('active_metrics'),
          dataIndex: 'metrics',
          key: 'metrics',
          render: (record: IdeaMetric[]) => (
            <div className="flex flex-wrap gap-1">
              {record.map((metric) => metric.metric?.name)?.join(', ')}
            </div>
          ),
        },
        {
          title: t('phase'),
          dataIndex: 'currentPhase',
          key: 'currentPhase',
          render: (_: unknown, record: Idea) => {
            return <span>{record.currentPhase?.phase.title}</span>;
          },
        },
      ];
  }, [metricId, t]);

  useEffect(() => {
    getData({
      variables: {
        channelId,
        projectId,
        challengeId,
        topicId,
        businessArea,
        countryId,
        metricId,
        page,
        limit: DEFAULT_LIMIT,
      },
    });
  }, [
    businessArea,
    challengeId,
    channelId,
    countryId,
    getData,
    page,
    projectId,
    topicId,
    metricId,
  ]);

  useEffect(() => {
    setPage(1);
  }, [metricId]);

  return (
    <>
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={data?.dashboardMetricIdeas.items}
        pagination={{
          current: page,
          total: data?.dashboardMetricIdeas.total || 0,
          pageSize: DEFAULT_LIMIT,
          showSizeChanger: false,
          onChange: (page) => setPage(page),
          position: ['bottomLeft'],
        }}
      />
      <Modal
        centered
        title={null}
        open={isOpenModal}
        onCancel={handleClickClose}
        width={1024}
        footer={null}
      >
        {selectedId && <IdeaChannelDetail id={selectedId} />}
      </Modal>
    </>
  );
}
